import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import PageHeader from '../../components/layout/PageHeader';
import Table from '../../components/ui/Table';
import Input from '../../components/ui/Input';
import CandidateFilterBar, { useCandidateFilters } from '../../components/filters/CandidateFilterBar';
import Button from '../../components/ui/Button';
import Modal from '../../components/ui/Modal';
import { DocumentThumbnail, DocumentPreviewModal } from '../../components/candidates/DocumentsTab';
import { downloadDocument, listDocumentCandidates, listCandidateDocuments } from '../../api/documents';
import SearchSummary, { withMatchColumn } from '../../components/ui/SearchMatch';

const IMAGE_EXT = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'avif'];

// Renders the actual file inline: images as <img>, PDFs in an embedded viewer. Anything the
// browser can't render (HEIC, Word, …) falls back to the file-type tile.
function InlinePreview({ doc, onOpen }) {
  const ext = (doc.original_name?.split('.').pop() || '').toLowerCase();
  const isPdf = doc.mime_type === 'application/pdf' || ext === 'pdf';
  const isImage =
    (doc.mime_type?.startsWith('image/') && !/hei[cf]$/.test(doc.mime_type)) || IMAGE_EXT.includes(ext);
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!isPdf && !isImage) return undefined;
    let objectUrl;
    let cancelled = false;
    downloadDocument(doc.id)
      .then((response) => {
        if (cancelled) return;
        const blob = isPdf ? new Blob([response.data], { type: 'application/pdf' }) : response.data;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [doc.id, isPdf, isImage]);

  if (!isPdf && !isImage) return <DocumentThumbnail doc={doc} onClick={onOpen} />;

  return (
    <div className="relative aspect-[4/3] w-full border-b border-gray-200 bg-gray-50">
      {!url ? (
        <div className="flex h-full items-center justify-center text-xs text-gray-400">Loading preview…</div>
      ) : isPdf ? (
        <iframe title={doc.original_name} src={`${url}#toolbar=0&navpanes=0`} className="h-full w-full" />
      ) : (
        <img src={url} alt={doc.original_name} className="h-full w-full object-cover" />
      )}
      <button
        type="button"
        onClick={onOpen}
        className="absolute right-2 top-2 rounded bg-white/90 px-2 py-1 text-xs font-medium text-gray-700 shadow hover:bg-white"
      >
        Enlarge
      </button>
    </div>
  );
}

function CandidateDocumentsModal({ candidate, onClose }) {
  const [previewDoc, setPreviewDoc] = useState(null);
  const docsQuery = useQuery({
    queryKey: ['candidate-documents', candidate?.id],
    queryFn: () => listCandidateDocuments(candidate.id),
    enabled: !!candidate,
  });
  const docs = docsQuery.data || [];

  return (
    <>
      <Modal
        open={!!candidate && !previewDoc}
        onClose={onClose}
        size="xl"
        title={candidate ? `Documents — ${candidate.full_name} (${candidate.reference_no})` : ''}
        footer={<Button variant="secondary" onClick={onClose}>Close</Button>}
      >
        {docsQuery.isLoading && <p className="py-8 text-center text-sm text-gray-500">Loading documents…</p>}
        {docsQuery.isError && <p className="py-8 text-center text-sm text-red-600">Could not load documents.</p>}
        {docsQuery.isSuccess && docs.length === 0 && (
          <p className="py-8 text-center text-sm text-gray-500">No documents uploaded for this candidate.</p>
        )}
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          {docs.map((doc) => (
            <div key={doc.id} className="overflow-hidden rounded-md border border-gray-200">
              <InlinePreview doc={doc} onOpen={() => setPreviewDoc(doc)} />
              <div className="p-2 text-xs">
                <div className="truncate font-medium text-gray-900" title={doc.original_name}>{doc.original_name}</div>
                <div className="text-gray-500">{doc.document_type_name}</div>
                <div className={doc.is_verified ? 'text-green-600' : 'text-gray-400'}>
                  {doc.is_verified ? 'Verified' : 'Not verified'}
                </div>
              </div>
            </div>
          ))}
        </div>
      </Modal>
      <DocumentPreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} readOnly />
    </>
  );
}

export default function CandidateDocuments() {
  const filters = useCandidateFilters();
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [viewing, setViewing] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const candidatesQuery = useQuery({
    queryKey: ['document-candidates', filters.params, search],
    queryFn: () => listDocumentCandidates({ ...filters.params, search: search || undefined }),
  });
  const candidates = useMemo(() => candidatesQuery.data || [], [candidatesQuery.data]);

  const columns = [
    { key: 'reference_no', label: 'Reference No' },
    { key: 'full_name', label: 'Full Name' },
    { key: 'workflow_category', label: 'Workflow' },
    { key: 'nationality', label: 'Nationality' },
    { key: 'contact_no', label: 'Contact' },
    {
      key: 'current_step',
      label: 'Current Step',
      render: (row) => row.current_step?.title || <span className="text-gray-400">—</span>,
    },
    { key: 'documents_count', label: 'Documents' },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <Button variant="secondary" onClick={() => setViewing(row)}>
          View documents
        </Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Documents"
        description="Find candidates by vehicle type, location, license type, workflow, step or name, and view the documents uploaded for them."
      />

      <CandidateFilterBar {...filters.barProps}>
        <Input placeholder="Search name, reference, contact…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="w-72" />
      </CandidateFilterBar>

      {(
        <>
          <SearchSummary search={search} total={candidates.length} rows={candidates} plainCount={`${candidates.length} shown`} />
          <Table columns={withMatchColumn(columns, search)} data={candidates} isLoading={candidatesQuery.isLoading} emptyMessage="No candidates match this filter." />
        </>
      )}

      <CandidateDocumentsModal candidate={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
